
























import { log } from '@/log'
import { OrderContext } from '@spa/components/pages/order/model/context/order-context'
import { PayloadSetPaymentToCreditcard, WithApiPayment } from '@spa/store/modules/orderConfirm/actions'
import dateFormat from 'date-fns/format'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'
import { Location, Route } from 'vue-router'

import SpaErrorDialog from '@/components/organisms/dialog/ErrorDialog.vue'
import { SelectOption, SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment as AvailablePayment, PaymentList } from '@/models/api/payment/payment'
import { PaymentType } from '@/models/app-config/behavior/types'
import { CartScopeEq } from '@/models/cart/group/scope'
import PaymentService from '@/services/api/payment'
import { getCreditcardPaymentProvierServiceConfig } from '@/services/payment'
import OptionsValidator, { ErrorBag } from '@/services/payment/options-validator'

import { InstanceResolver } from '@/util/instanceResolver'
import PaymentEditViewModel,
  { PaymentEditViewModelFactory } from '@spa/components/organisms/order/edit/payment/model/payment-edit-view-model'
import PaymentEdit from '@spa/components/organisms/order/edit/payment/PaymentEdit.vue'
import { toQuery, toScope } from '@spa/components/pages/order/scope-transpiler'
import LoadingMixin, { Status } from '@spa/mixins/loading'
import { PluginMixin } from '@spa/mixins/plugin'
import { OrderList, OrderService, OrderSource } from '@spa/services/order'
import store from '@spa/store'
import { CartItem } from '@spa/store/modules/cart/types'
import { CreditcardModel } from '@spa/store/modules/creditcard/types'
import { Payment } from '@spa/store/modules/orderConfirm/types'
import SelectedPayment from './model/selected-payment'

import * as CurrentTime from '@/models/current-time/functions'
import { EventBusForThirdParty } from '@/usecases/event-bus-for-third-party/event-bus'
import { User } from '@spa/store/modules/user/types'

Component.registerHooks([
  'beforeRouteEnter',
])

@Component({
  components: {
    PaymentEdit,
    SpaErrorDialog,
  },
  mixins: [
    LoadingMixin,
  ],
})
export default class Index extends Mixins<PluginMixin>(PluginMixin) {
  @Prop({ required: true })
  scope: CartScopeEq

  @Prop({ required: true })
  context: OrderContext

  Status = Status
  pageStatus: Status = Status.Pending
  removeCardStatus: Status = Status.Pending
  /**
   * 選択された支払い方法に関するバリデーション結果。
   * ただし現在はコンビニ決済、キャリア決済のバリデーション結果しか入っていない。
   * また、モーダル表示に関する既存のエラーハンドリングとの統合も要検討。
   * @see submitDefault
   */
  selectedPaymentOptionsValidation: ErrorBag = ErrorBag.empty()

  showModal: boolean = false
  modalMessage: string = ''

  readonly cardPaymentProvider = getCreditcardPaymentProvierServiceConfig().forPurchase
  readonly orderService = OrderService.create({
    orderSource: OrderSource.asUndefined(),
  })
  readonly paymentService = new PaymentService()

  availablePaymentList: PaymentList = PaymentList.empty()
  selected: SelectedPayment = SelectedPayment.empty()

  get user(): User {
    return this.$store.state.user.user
  }

  get brandEnglishName(): string | null {
    return this.scope.kind.isBrand ? this.scope.value.toString() : null
  }

  get isLoading() {
    return this.pageStatus === Status.Loading || this.removeCardStatus === Status.Loading
  }

  get cartItems(): CartItem[] {
    const items: CartItem[] = this.$store.getters['cart/itemsWithFilter'](
      this.filterPurchasableItem
    )

    return this.context.filter.cartItems(items)
  }

  get orderList(): OrderList {
    return new OrderList(
      this.orderService.createOrderFromCartItems(this.cartItems, this.deliveryDateForStandardOrder)
    )
  }

  get deliveryDate(): Date | null {
    const deliveryDate = this.$store.state.orderConfirm.deliveryDate

    return _.get(deliveryDate, 'deliveryPlanDate', null)
  }

  get deliveryDateForStandardOrder(): string {
    return this.deliveryDate === null ? '未指定' : dateFormat(this.deliveryDate, 'YYYY年MM月DD日')
  }

  get creditcards(): CreditcardModel[] {
    return this.$store.state.creditcard.items
  }

  get creditcardError(): { load?: Error } {
    return _.get(this.$store.state.creditcard, 'error')
  }

  get creditcardLoadError(): Error|null {
    return _.get(this.creditcardError, 'load', null)
  }

  get creditcardLoadFailed(): boolean {
    return this.creditcardLoadError !== null
  }

  get paymentType(): PaymentType | '' {
    return this.selected.paymentType
  }

  get payment(): Payment {
    return _.get(this.$store.state.orderConfirm, 'payment')
  }

  get selectedPaymentOptions(): SelectOptionList {
    return this.selected.optionList
  }

  get selectedPaymentTypeIsAllowed(): boolean {
    return this.availablePaymentList.isAvailablePayment(this.paymentType)
  }

  get paymentEditViewModel(): PaymentEditViewModel {
    const factory = new PaymentEditViewModelFactory(
      this.availablePaymentList,
      this.creditcards,
      this.useCreditcard,
      this.removeCreditcard,
      this.navigateToNewCreditCard,
      false
    )
    factory.selectedPaymentId = this.paymentType
    factory.selectedOptions = this.selectedPaymentOptions
    factory.creditCardLoadFailed = this.creditcardLoadFailed
    factory.creditCardRegisterLimit = this.cardPaymentProvider.registerCardLimit
    factory.creditCardSelectedId = (this.payment && this.payment.token) || ''
    factory.caption = 'お支払い方法を選択してください。'
    factory.cancelButtonLabel = '注文画面に戻る'
    factory.showCancelButton = !!this.payment

    return factory.create()
  }

  get paymentListIsEmpty(): boolean {
    return this.availablePaymentList.isEmpty
  }

  get cancelUrl(): string {
    // 利用できる決済方法がない場合はカートを変更する必要があるのでカートに戻る
    if (this.paymentListIsEmpty) {
      return this.cartUrl
    }

    return this.context.path.confirmPage
  }

  get cartUrl(): string {
    if (InstanceResolver.isSingle()) {
      return '/cart'
    }

    return '/carts'
  }

  async beforeRouteEnter (to: Route, _from: Route, next) {
    // TODO: ルーティング定義側に移動。
    // 特に強い事情が無ければ、ルーティング定義の方にhook関数として定義を移動する。
    const scope = toScope(to.query.scope as string || '')
    const redirect: Location | undefined = await guardOnCart(scope)

    if (redirect) {
      next(redirect)
    }

    next()
  }

  async created() {
    this.pageStatus = Status.Loading

    await this.$store.dispatch('cart/load')
    await this.fetchAvailablePaymentList()

    await this.$store.dispatch('creditcard/load')
    this.loadInitialPayment()

    await this.$store.dispatch('user/get')
    this.pageStatus = Status.Completed

    EventBusForThirdParty.getInstance().publish('page-initialized', {
      url: window.location.href,
      initializedAt: CurrentTime.create().now,
      user: this.user,
    })
  }

  isAvailablePayment(paymentId: string) {
    return this.availablePaymentList.isAvailablePayment(paymentId)
  }

  loadInitialPayment(): void {
    if (!this.payment) {
      return
    }

    const payment = this.availablePaymentList.find(p => p.id === this.payment.payment_type)
    if (!payment) {
      return
    }

    this.selected = SelectedPayment
      .createByPayment(payment)
      .update(this.payment.selectedOptionList || SelectOptionList.empty())
  }

  filterPurchasableItem(item: CartItem): boolean {
    return !(item.removed || false)
  }

  async validateAll(): Promise<boolean> {
    return this.$validator.validateAll()
  }

  get selectedPayment(): AvailablePayment | null {
    return this.selected.payment
  }

  async submitOtherThanCreditCard() {
    // validate fields
    const validated = await this.validateAll()
    // TODO $validatorにエラーがあるように見えるがvalidatedが常にtrue
    if (!validated) {
      this.modalMessage = 'ご入力内容に不備があります。'
      this.showModal = true

      return
    }

    // validate payment type
    if (!this.paymentType) {
      this.modalMessage = 'お支払い方法が選択されていません'
      this.showModal = true

      return
    }

    if (!this.selectedPaymentTypeIsAllowed) {
      this.modalMessage = 'このお支払い方法はご利用頂けません'
      this.showModal = true

      return
    }

    /**
     * このメソッド名はsubmitOtherThanCreditCardではあるが、
     * すでにクレジットカードを選択した状態で他の支払い方法に変更せずに支払い方法確定ボタンを押すことは可能である。
     *
     * その場合はVuexのPaymentを更新する必要はないので何もせずに注文確認画面に遷移すればよい。
     * - クレジットカードが選択された場合、PaymentEdit内でVuexにsetされ、注文確認画面に遷移する
     * - したがってクレジットカードが選択中でこのメソッドが呼ばれるということは、この画面で何も変更されていない場合に限られる
     *
     * なお、支払い方法確定ボタンはPaymentEdit内に隠蔽されているが、
     * - クレジットカードが選択されているときにボタンを押せなくするわけではない
     * - PaymentEditとしてはsubmitイベントをemitしているに過ぎない（クレジットカードの場合にemitしないことが表現されていない）
     * ので、現状の作りではPaymentEditのemitするsubmitイベントをsubmitOtherThanCreditCardとして扱っている
     * 本コンポーネント側に問題があると言える、かもしれない。
     *
     * いずれにせよ以下の課題を残しながらの暫定対応という位置づけです。
     * - UIとロジックが暗黙に結合してしまっている
     * - そもそもUIから改善を検討してもよさそう
     *
     * @see https://github.com/Everforth/growthpalette-front/issues/8414#issuecomment-2792602531
     */
    if (this.paymentType === 'creditcard3d') {
      this.navigateToOrder()

      return
    }

    if (this.paymentType === 'cash-on-delivery') {
      await this.submitCashOnDelivery()

      return
    }

    if (this.paymentType === 'np-postpay') {
      await this.submitNpPostpay()

      return
    }

    if (this.paymentType === 'atone') {
      await this.submitAtone()

      return
    }

    if (this.paymentType === 'paidy') {
      await this.submitPaidy()

      return
    }

    // コンビニ決済、キャリア決済時に実装されたルート
    // それ以降に実装された決済方法は原則このルートを利用する
    // 既存の決済方法もこのルートに寄せていきたい
    await this.submitDefault()
  }

  async submitCashOnDelivery(): Promise<void> {
    const payload: WithApiPayment = {
      apiPayment: this.selectedPayment!,
    }
    await this.$store.dispatch('orderConfirm/setPaymentToCashOnDelivery', payload)

    this.navigateToOrder()
  }

  async submitNpPostpay(): Promise<void> {
    const payload: WithApiPayment = {
      apiPayment: this.selectedPayment!,
    }
    await this.$store.dispatch('orderConfirm/setPaymentToNpPostpay', payload)

    this.navigateToOrder()
  }

  async submitAtone(): Promise<void> {
    const payload: WithApiPayment = {
      apiPayment: this.selectedPayment!,
    }
    await this.$store.dispatch('orderConfirm/setPaymentToAtone', payload)

    this.navigateToOrder()
  }

  async submitPaidy(): Promise<void> {
    const payload: WithApiPayment = {
      apiPayment: this.selectedPayment!,
    }
    await this.$store.dispatch('orderConfirm/setPaymentToPaidy', payload)

    this.navigateToOrder()
  }

  // コンビニ決済、キャリア決済時に実装されたルート
  // それ以降に実装された決済方法は原則このルートを利用する
  // 既存の決済方法もこのルートに寄せていきたい
  async submitDefault(): Promise<void> {
    if (this.selectedPayment === null) {
      return
    }

    this.selectedPaymentOptionsValidation = (new OptionsValidator(
      this.selectedPayment.parameters,
      this.selectedPaymentOptions
    )).validate()

    if (this.selectedPaymentOptionsValidation.hasSomeError) {
      this.modalMessage = 'ご入力内容に不備があります。'
      this.showModal = true

      return
    }

    const payment: Payment = {
      payment_type: this.selectedPayment.id,
      apiPayment: this.selectedPayment,
      selectedOptionList: this.selectedPaymentOptions,
    }

    await this.$store.dispatch('orderConfirm/setPayment', { payment })

    this.navigateToOrder()
  }

  useCreditcard(creditcard: CreditcardModel): () => Promise<void> {
    return async () => {
      const cardPayment = this.availablePaymentList.find(p => p.isCreditCard())
      if (!(cardPayment && cardPayment.isCreditCard())) {
        throw new Error(`expected type is kind of credit card, but actual is ${cardPayment}`)
      }

      const payload: PayloadSetPaymentToCreditcard = Object.assign(creditcard.clone(), {
        token: creditcard.id,
        payment_type: cardPayment.id,
        apiPayment: cardPayment,
      })
      await this.$store.dispatch('orderConfirm/setPaymentToCreditcard', payload)

      this.navigateToOrder()
    }
  }

  removeCreditcard(creditcard: CreditcardModel): () => Promise<void> {
    return async () => {
      this.removeCardStatus = Status.Loading

      const { id } = creditcard

      await this.$store.dispatch('creditcard/remove', id)

      const errorOnRemove = _.find(this.$store.state.creditcard.items, ['id', id])

      if (errorOnRemove) {
        this.removeCardStatus = Status.Failed
        this.modalMessage = 'エラーが発生しました。<br/>クレジットカード情報が削除できませんでした。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
        this.showModal = true

        return
      }

      await this.$store.dispatch('creditcard/load', { force: true })

      // Remove from orderConfirm if the creditcard is used
      if (this.payment && this.payment.token === id) {
        await this.$store.dispatch('orderConfirm/unsetPayment')
      }

      this.removeCardStatus = Status.Completed
    }
  }

  onPaymentSelected(payment: AvailablePayment) {
    if (!this.availablePaymentList.isAvailablePayment(payment.id)) {
      return
    }

    this.selected = SelectedPayment.createByPayment(payment)
  }

  onOptionSelected(option: SelectOption) {
    this.selected = this.selected.toggleOption(option)
  }

  navigateToNewCreditCard() {
    this.$router.push(this.context.path.addCreditCard)
  }

  onCanceled() {
    this.$router.push(this.cancelUrl)
  }

  onPaymentEditError(message: string) {
    this.modalMessage = message
    this.showModal = true
  }

  private navigateToOrder() {
    this.$router.push(this.context.path.confirmPage)
  }

  private async fetchAvailablePaymentList() {
    const cartIdList = _.map(this.cartItems, cart => cart.id)
    const result = await this.paymentService.listAllowedPaymentsFor(cartIdList, {
      confirm_url: `${location.origin}/s/order?scope=${toQuery(this.scope)}`,
    })

    if (result.isLeft()) {
      this.modalMessage = 'エラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
      this.showModal = true
      log.error({ error: result.value })

      return
    }

    this.availablePaymentList = PaymentList.valueOf(result.value)
  }
}

function getCartRedirectLocation(): Location {
  if (InstanceResolver.isSingle()) {
    return { path: '/cart' }
  }

  return { path: '/carts' }
}

async function guardOnCart(scope: CartScopeEq): Promise<Location | undefined> {
  await store.dispatch('cart/loadWithScope', { scope })

  const error = _.get(store.state.cart.error, 'load')

  const diplayItems: CartItem[] = store.getters['cart/displayItems']

  const totalItemCount = _.sumBy(diplayItems, item => _.toNumber(item.unitCount))

  if (error || totalItemCount === 0) {
    return getCartRedirectLocation()
  }
}
